import React, { useState } from 'react';
import {
  BookOpenIcon,
  KeyIcon,
  CodeBracketIcon,
  UserCircleIcon,
  ArrowUturnLeftIcon,
  ExclamationTriangleIcon,
  BoltIcon,
  ShieldCheckIcon,
  CubeTransparentIcon,
  MegaphoneIcon,
  QueueListIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Card, CardHeader, Badge } from '../components/ui';
import { cn } from '../utils/helpers';
import {
  CodeBlock,
  Callout,
  CollapsibleSection,
  EndpointCard,
  FieldTable,
  Mono,
  SectionHeading,
  SimpleTable,
} from '../components/docs/DocPrimitives';
import {
  WebhookOverview,
  WebhookConfiguration,
  WebhookSecurity,
  WebhookPayload,
  WebhookAttribution,
  WebhookEvents,
  WebhookReliability,
} from '../components/docs/WebhookDocs';

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

type ProductId = 'checkout' | 'webhooks';

const CHECKOUT_SECTIONS = [
  { id: 'overview', name: 'Overview', icon: BookOpenIcon },
  { id: 'authentication', name: 'Authentication', icon: KeyIcon },
  { id: 'request', name: 'Request Payload', icon: CodeBracketIcon },
  { id: 'customer', name: 'Customer Fields', icon: UserCircleIcon, isNew: true },
  { id: 'response', name: 'Response', icon: ArrowUturnLeftIcon },
  { id: 'errors', name: 'Errors', icon: ExclamationTriangleIcon },
];

const WEBHOOK_SECTIONS = [
  { id: 'wh-overview', name: 'Overview', icon: BookOpenIcon },
  { id: 'wh-configuration', name: 'Configuration', icon: QueueListIcon },
  { id: 'wh-security', name: 'Security', icon: ShieldCheckIcon },
  { id: 'wh-payload', name: 'Payload', icon: CubeTransparentIcon },
  { id: 'wh-events', name: 'Event Catalog', icon: BoltIcon },
  { id: 'wh-attribution', name: 'Attribution', icon: MegaphoneIcon, isNew: true },
  { id: 'wh-reliability', name: 'Retries & Practices', icon: ArrowPathIcon },
];

const PRODUCTS: Record<ProductId, {
  label: string;
  title: string;
  subtitle: string;
  badges: { label: string; variant: 'primary' | 'secondary' | 'accent' }[];
  sections: typeof CHECKOUT_SECTIONS;
}> = {
  checkout: {
    label: 'Checkout API',
    title: 'Checkout API',
    subtitle: 'Create a hosted checkout session for your customers from your own storefront',
    badges: [
      { label: 'v2', variant: 'primary' },
      { label: 'Partner API', variant: 'secondary' },
    ],
    sections: CHECKOUT_SECTIONS,
  },
  webhooks: {
    label: 'Webhooks',
    title: 'Webhooks',
    subtitle: 'Real-time events for every order, charge, prescription and shipment in your funnel',
    badges: [
      { label: '2026-09-01', variant: 'primary' },
      { label: 'Outbound', variant: 'secondary' },
    ],
    sections: WEBHOOK_SECTIONS,
  },
};

export const Documentation: React.FC = () => {
  const [activeProduct, setActiveProduct] = useState<ProductId>('checkout');
  const [activeSection, setActiveSection] = useState('overview');

  const product = PRODUCTS[activeProduct];

  const selectProduct = (next: ProductId) => {
    setActiveProduct(next);
    setActiveSection(PRODUCTS[next].sections[0].id);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">{product.title}</h1>
          <p className="text-secondary-500 dark:text-secondary-400">{product.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {product.badges.map((badge) => (
            <Badge key={badge.label} variant={badge.variant}>{badge.label}</Badge>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Navigation Sidebar */}
        <div className="lg:col-span-1 space-y-4">
          {/* Product switcher */}
          <Card padding="sm">
            <nav className="space-y-1">
              {(Object.keys(PRODUCTS) as ProductId[]).map((id) => (
                <button
                  key={id}
                  onClick={() => selectProduct(id)}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors',
                    activeProduct === id
                      ? 'bg-primary-600 text-white'
                      : 'text-secondary-600 dark:text-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-700 hover:text-secondary-900 dark:hover:text-secondary-100'
                  )}
                >
                  {id === 'webhooks' ? (
                    <BoltIcon className="w-4 h-4 shrink-0" />
                  ) : (
                    <CodeBracketIcon className="w-4 h-4 shrink-0" />
                  )}
                  <span className="flex-1 text-left">{PRODUCTS[id].label}</span>
                </button>
              ))}
            </nav>
          </Card>

          <Card padding="sm">
            <nav className="space-y-1">
              {product.sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    activeSection === section.id
                      ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                      : 'text-secondary-600 dark:text-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-700 hover:text-secondary-900 dark:hover:text-secondary-100'
                  )}
                >
                  <section.icon className="w-4 h-4 shrink-0" />
                  <span className="flex-1 text-left">{section.name}</span>
                  {section.isNew && <Badge variant="accent" className="text-[10px]">New</Badge>}
                </button>
              ))}
            </nav>
          </Card>
        </div>

        {/* Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* ------------------------------------------------------------- */}
          {/*  Overview                                                     */}
          {/* ------------------------------------------------------------- */}
          {activeSection === 'overview' && (
            <Card>
              <CardHeader
                title="Overview"
                subtitle="How the V2 checkout session works end to end"
              />

              <div className="space-y-6">
                <p className="text-secondary-600 dark:text-secondary-400">
                  The Checkout API lets your platform hand a fully-configured cart to Capture Health.
                  You post the products, pricing, billing frequency and shipping choices you want the
                  customer to see, and the API returns a redirect URL to the Capture Health intake
                  portal where the customer completes their medical intake, enters payment and places
                  the order.
                </p>

                <div>
                  <SectionHeading>Endpoint</SectionHeading>
                  <div className="mt-3">
                    <EndpointCard
                      method="POST"
                      endpoint="/partner/checkout"
                      description="Creates a checkout session and returns the URL to redirect your customer to."
                    />
                  </div>
                </div>

                <div>
                  <SectionHeading>Base URLs</SectionHeading>
                  <div className="mt-3 space-y-3">
                    <CodeBlock code="https://api.capturehealth.io/partner/checkout" title="Production" language="http" />
                    <CodeBlock code="https://api.capturehealth-dev.io/partner/checkout" title="Development" language="http" />
                  </div>
                </div>

                <div>
                  <SectionHeading>Integration flow</SectionHeading>
                  <div className="mt-3 space-y-4">
                    {[
                      {
                        title: 'Build the cart on your site',
                        body: 'The customer picks products, a supply length and a shipping option in your own UI.',
                      },
                      {
                        title: 'Create a checkout session',
                        body: 'POST the cart to /partner/checkout from your server with your API key. Include anything you already know about the customer in the customer object.',
                      },
                      {
                        title: 'Redirect the customer',
                        body: 'Send the customer to the returned redirectUrl. Pre-filled details appear in the checkout form; the customer can still edit them.',
                      },
                      {
                        title: 'Capture Health takes it from there',
                        body: 'Intake questionnaire, provider review, payment and pharmacy fulfillment all happen on the Capture Health platform.',
                      },
                    ].map((step, index) => (
                      <div key={step.title} className="flex items-start gap-4">
                        <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 flex items-center justify-center font-bold text-sm shrink-0">
                          {index + 1}
                        </div>
                        <div>
                          <h4 className="font-medium text-secondary-900 dark:text-secondary-100">{step.title}</h4>
                          <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">{step.body}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <SectionHeading>Minimal example</SectionHeading>
                  <div className="mt-3">
                    <CodeBlock
                      language="bash"
                      title="Create a checkout session"
                      code={`curl -X POST "https://api.capturehealth.io/partner/checkout" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "frequency": 90,
    "items": [
      {
        "offeringId": "dosage-variety-uuid",
        "quantity": 6,
        "pricing": { "listPrice": 149.99, "finalPrice": 129.99 }
      }
    ],
    "customer": {
      "firstName": "Jane",
      "lastName": "Doe",
      "email": "jane.doe@example.com"
    }
  }'`}
                    />
                  </div>
                </div>

                <Callout title="New in this release: customer pre-population">
                  <p>
                    You can now send an optional <Mono>customer</Mono> object with the details you
                    already collected, and the intake portal pre-fills those checkout inputs. See the{' '}
                    <button
                      onClick={() => setActiveSection('customer')}
                      className="underline font-medium text-primary-700 dark:text-primary-400"
                    >
                      Customer Fields
                    </button>{' '}
                    section.
                  </p>
                </Callout>
              </div>
            </Card>
          )}

          {/* ------------------------------------------------------------- */}
          {/*  Authentication                                               */}
          {/* ------------------------------------------------------------- */}
          {activeSection === 'authentication' && (
            <Card>
              <CardHeader
                title="Authentication"
                subtitle="Every Partner API request is authenticated with your company API key"
              />

              <div className="space-y-6">
                <div>
                  <SectionHeading>API key header</SectionHeading>
                  <p className="text-secondary-600 dark:text-secondary-400 mt-2 mb-3">
                    Send your API key in the <Mono>x-api-key</Mono> header. The key identifies your
                    company and determines which product offerings are available to the session.
                  </p>
                  <CodeBlock code={`x-api-key: YOUR_API_KEY`} title="Header format" language="http" />
                </div>

                <div>
                  <SectionHeading>Getting your API key</SectionHeading>
                  <ol className="list-decimal list-inside space-y-2 text-secondary-600 dark:text-secondary-400 mt-2">
                    <li>Go to <strong>Configurations</strong> in the left navigation</li>
                    <li>Find the <strong>Capture Health API Key</strong> panel</li>
                    <li>Click <strong>Enable API Access</strong> if you have not already</li>
                    <li>Copy the key — the full value is only shown once</li>
                    <li>Store it as a server-side secret; you can rotate or revoke it from the same panel</li>
                  </ol>
                </div>

                <Callout variant="warning" title="Server-side only">
                  <p>
                    Your API key can create checkout sessions with any pricing you specify. Never ship
                    it in browser JavaScript, a mobile app, or anywhere a customer could read it —
                    call <Mono>/partner/checkout</Mono> from your backend and redirect the browser to
                    the URL it returns.
                  </p>
                </Callout>

                <div>
                  <SectionHeading>Authentication errors</SectionHeading>
                  <div className="mt-3">
                    <SimpleTable
                      headers={['Status', 'Response', 'Cause']}
                      rows={[
                        ['401', <Mono>{`{ "error": "API key is required" }`}</Mono>, 'The x-api-key header was missing'],
                        ['401', <Mono>{`{ "error": "Invalid API key" }`}</Mono>, 'The key is unknown, revoked, or rotated'],
                        ['429', <Mono>{`{ "error": "Too many requests" }`}</Mono>, 'Rate limit exceeded for this key'],
                      ]}
                    />
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* ------------------------------------------------------------- */}
          {/*  Request payload                                              */}
          {/* ------------------------------------------------------------- */}
          {activeSection === 'request' && (
            <Card>
              <CardHeader
                title="Request Payload"
                subtitle="POST /partner/checkout — application/json"
              />

              <div className="space-y-6">
                <div>
                  <SectionHeading>Top-level fields</SectionHeading>
                  <p className="text-secondary-600 dark:text-secondary-400 mt-2 mb-3">
                    At least one <Mono>item</Mono> or <Mono>bundle</Mono> must be provided, and{' '}
                    <Mono>frequency</Mono> is always required.
                  </p>
                  <FieldTable
                    fields={[
                      { name: 'frequency', type: 'number', required: true, description: 'Billing frequency in days (30, 60, 90…). Also determines which offering is looked up, via monthSupply = frequency / 30.' },
                      { name: 'items', type: 'PrescriptionItem[]', description: 'Individual prescription products. Required if no bundles are sent.' },
                      { name: 'bundles', type: 'Bundle[]', description: 'Groups of products sold together at a combined price. Required if no items are sent.' },
                      { name: 'isSubscription', type: 'boolean', description: 'Defaults to true. Set false for a one-time purchase with no recurring charge.' },
                      { name: 'shippingOptions', type: 'ShippingOption[]', description: 'Shipping choices shown at checkout (1–10 options).' },
                      { name: 'campaign', type: 'CampaignInfo', description: 'Marketing attribution. UTM parameters are appended to the redirect URL.' },
                      { name: 'customer', type: 'Customer', description: 'Details you already know about the customer, used to pre-fill the checkout form. New in this release.' },
                    ]}
                  />
                </div>

                <div className="space-y-4">
                  <SectionHeading>Object reference</SectionHeading>

                  <CollapsibleSection title="PrescriptionItem" defaultOpen>
                    <FieldTable
                      fields={[
                        { name: 'offeringId', type: 'string', required: true, description: 'The DosageVariety ID — not the CompanyOffering ID. See Offering lookup below.' },
                        { name: 'quantity', type: 'number', required: true, description: 'Quantity used for the offering lookup.' },
                        { name: 'refillQuantity', type: 'number', description: 'Optional refill quantity.' },
                        { name: 'pricing', type: 'Pricing', required: true, description: 'listPrice, finalPrice and optional discountAmount.' },
                        { name: 'discounts', type: 'Discount[]', description: 'Display-only discount breakdown shown in the checkout UI.' },
                      ]}
                    />
                    <CodeBlock
                      language="json"
                      title="Example item"
                      code={`{
  "offeringId": "dosage-variety-uuid",
  "quantity": 12,
  "pricing": {
    "listPrice": 199.99,
    "finalPrice": 179.99,
    "discountAmount": 99.99
  },
  "discounts": [
    {
      "type": "coupon",
      "code": "FIRST50",
      "description": "50% off first order",
      "amount": 50,
      "appliedAmount": 80.00
    }
  ]
}`}
                    />
                  </CollapsibleSection>

                  <CollapsibleSection title="Pricing">
                    <p className="text-sm text-secondary-600 dark:text-secondary-400">
                      Pricing is a three-tier model. <Mono>listPrice</Mono> is shown struck through,
                      the customer pays <Mono>discountAmount</Mono> on the first order (when present)
                      and <Mono>finalPrice</Mono> on every order after that.
                    </p>
                    <FieldTable
                      fields={[
                        { name: 'listPrice', type: 'number', required: true, description: 'Original retail price, used for display.' },
                        { name: 'finalPrice', type: 'number', required: true, description: 'Standard recurring price. Cannot exceed listPrice.' },
                        { name: 'discountAmount', type: 'number', description: 'First-order promotional price. Cannot be negative or exceed listPrice.' },
                      ]}
                    />
                    <CodeBlock
                      language="text"
                      title="Charge logic"
                      code={`First order:  discountAmount ?? finalPrice
Recurring:    finalPrice
Display:      listPrice (crossed out) → discountAmount / finalPrice`}
                    />
                  </CollapsibleSection>

                  <CollapsibleSection title="Bundle">
                    <FieldTable
                      fields={[
                        { name: 'id', type: 'string', required: true, description: 'Your identifier for the bundle.' },
                        { name: 'name', type: 'string', required: true, description: 'Display name shown at checkout.' },
                        { name: 'description', type: 'string', description: 'Optional supporting copy.' },
                        { name: 'items', type: 'BundleItem[]', required: true, description: 'Each entry carries offeringId, quantity and optional refillQuantity.' },
                        { name: 'pricing', type: 'Pricing', required: true, description: 'Combined bundle pricing, plus optional bundleSavings for display.' },
                        { name: 'discounts', type: 'Discount[]', description: 'Display-only discount breakdown.' },
                      ]}
                    />
                    <CodeBlock
                      language="json"
                      title="Example bundle"
                      code={`{
  "id": "weight-loss-bundle",
  "name": "Complete Weight Loss Kit",
  "description": "Everything you need to start your journey",
  "items": [
    { "offeringId": "medication-a-dosage-variety", "quantity": 6 },
    { "offeringId": "supplement-b-dosage-variety", "quantity": 30 }
  ],
  "pricing": {
    "listPrice": 399.99,
    "finalPrice": 349.99,
    "bundleSavings": 50.00
  }
}`}
                    />
                  </CollapsibleSection>

                  <CollapsibleSection title="ShippingOption">
                    <FieldTable
                      fields={[
                        { name: 'carrier', type: "'ups' | 'usps' | 'fedex'", required: true, description: 'Shipping carrier.' },
                        { name: 'method', type: 'string', required: true, description: 'Method code for the carrier (see the list below).' },
                        { name: 'price', type: 'number', required: true, description: 'Standard shipping price. Must be 0 or greater.' },
                        { name: 'discountAmount', type: 'number', description: 'The discounted price paid on the first order — not the amount subtracted. Must be between 0 and price.' },
                        { name: 'deliveryTimeframe', type: 'string', description: 'Customer-facing estimate, e.g. "3-5 business days".' },
                      ]}
                    />
                    <CodeBlock
                      language="text"
                      title="Valid method codes"
                      code={`USPS:  usps_priority, usps_first, usps_priority_express,
       usps_ground, usps_ground_advantage
UPS:   ups_ground, ups_3day, ups_2day, ups_nextday
FedEx: fedex_ground, fedex_2day, fedex_3day, fedex_nextday,
       fedex_standard_overnight, fedex_priority_overnight, fedex_2_day`}
                    />
                  </CollapsibleSection>

                  <CollapsibleSection title="CampaignInfo">
                    <FieldTable
                      fields={[
                        { name: 'code', type: 'string', description: 'Campaign code.' },
                        { name: 'source', type: 'string', description: 'Traffic source.' },
                        { name: 'medium', type: 'number', description: 'Medium identifier.' },
                        { name: 'campaign', type: 'string', description: 'Campaign name.' },
                        { name: 'content', type: 'string', description: 'Content identifier.' },
                        { name: 'term', type: 'string', description: 'Search term or keyword.' },
                        { name: 'coupon', type: 'string', description: 'Coupon code.' },
                        { name: 'affiliateId', type: 'string', description: 'Affiliate identifier.' },
                        { name: 'utmParameters', type: 'object', description: 'utm_source, utm_medium, utm_campaign, utm_term, utm_content — appended to the redirect URL.' },
                        { name: 'customData', type: 'object', description: 'Any additional tracking data you want stored with the session.' },
                      ]}
                    />
                  </CollapsibleSection>
                </div>

                <div>
                  <SectionHeading>Offering lookup</SectionHeading>
                  <p className="text-secondary-600 dark:text-secondary-400 mt-2 mb-3">
                    <Mono>offeringId</Mono> is a <strong>DosageVariety ID</strong>. The API resolves it
                    to one of your active offerings using the ID together with the quantity and the
                    month supply derived from <Mono>frequency</Mono>. If nothing matches, the request
                    fails rather than creating a session.
                  </p>
                  <CodeBlock
                    language="text"
                    title="Lookup key"
                    code={`CompanyOffering = {
  companyId:       <from your API key>,
  dosageVarietyId: <item.offeringId>,
  quantity:        <item.quantity>,
  monthSupply:     <frequency / 30>,
  isActive:        true
}`}
                  />
                </div>

                <div>
                  <SectionHeading>Subscription vs one-time</SectionHeading>
                  <div className="mt-3">
                    <SimpleTable
                      headers={['isSubscription', 'Behavior']}
                      rows={[
                        [<Mono>true</Mono>, 'Default. Recurring subscription — the customer is charged every frequency days.'],
                        [<Mono>false</Mono>, 'One-time purchase. The customer is charged once, with no recurring billing.'],
                      ]}
                    />
                  </div>
                  <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-3">
                    <Mono>frequency</Mono> is required either way, because it selects the supply length
                    of the offering. Set <Mono>isSubscription: false</Mono> explicitly for one-time
                    purchases rather than relying on the default.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* ------------------------------------------------------------- */}
          {/*  Customer fields (new)                                        */}
          {/* ------------------------------------------------------------- */}
          {activeSection === 'customer' && (
            <Card>
              <CardHeader
                title="Customer Fields"
                subtitle="Optional customer details that pre-fill the checkout form"
                action={<Badge variant="accent">New</Badge>}
              />

              <div className="space-y-6">
                <Callout title="What this does">
                  <p>
                    When you include a <Mono>customer</Mono> object, those values are stored on the
                    checkout session and pre-filled into the matching inputs when the customer lands
                    on the intake portal. Anything you leave out is collected from the customer as
                    usual. Nothing is locked — the customer can correct any pre-filled value before
                    placing the order.
                  </p>
                </Callout>

                <div>
                  <SectionHeading>Fields</SectionHeading>
                  <p className="text-secondary-600 dark:text-secondary-400 mt-2 mb-3">
                    Every field is optional. Send whatever you already know.
                  </p>
                  <FieldTable
                    fields={[
                      { name: 'firstName', type: 'string', description: 'Max 100 characters.' },
                      { name: 'lastName', type: 'string', description: 'Max 100 characters.' },
                      { name: 'email', type: 'string', description: 'Valid email address, max 255 characters. Stored lower-cased.' },
                      { name: 'phone', type: 'string', description: 'US phone number in any formatting. Normalized to 10 digits.' },
                      { name: 'dateOfBirth', type: 'string', description: 'YYYY-MM-DD or MM/DD/YYYY. Must be a real date in the past, within the last 120 years.' },
                      { name: 'gender', type: "'male' | 'female'", description: "'m' and 'f' are also accepted, in any casing." },
                      { name: 'heightCm', type: 'number', description: 'Between 30 and 275. Only used for patients aged 18 or under.' },
                      { name: 'weightKg', type: 'number', description: 'Between 1 and 1000. Only used for patients aged 18 or under.' },
                      { name: 'address', type: 'Address', description: 'Shipping address. All-or-nothing — see below.' },
                    ]}
                  />
                </div>

                <div>
                  <SectionHeading>customer.address</SectionHeading>
                  <p className="text-secondary-600 dark:text-secondary-400 mt-2 mb-3">
                    The address object is optional, but if you send it,{' '}
                    <Mono>address1</Mono>, <Mono>city</Mono>, <Mono>state</Mono> and <Mono>zipCode</Mono>{' '}
                    are all required — a half-filled address would leave the customer with a partially
                    completed form. Only US addresses are supported.
                  </p>
                  <FieldTable
                    fields={[
                      { name: 'address1', type: 'string', required: true, description: 'Street address, max 200 characters.' },
                      { name: 'address2', type: 'string', description: 'Apartment, suite, etc. Max 200 characters.' },
                      { name: 'city', type: 'string', required: true, description: 'Max 100 characters.' },
                      { name: 'state', type: 'string', required: true, description: 'Two-letter US state code (DC included). Case-insensitive.' },
                      { name: 'zipCode', type: 'string', required: true, description: '5-digit ZIP or ZIP+4. Stored as the 5-digit form.' },
                      { name: 'country', type: 'string', description: "Defaults to 'US'. Only 'US' / 'USA' is accepted." },
                    ]}
                  />
                </div>

                <div>
                  <SectionHeading>Where each value lands</SectionHeading>
                  <div className="mt-3">
                    <SimpleTable
                      headers={['Customer field', 'Checkout field']}
                      rows={[
                        [<Mono>firstName, lastName</Mono>, 'First / Last Name'],
                        [<Mono>email</Mono>, 'Email'],
                        [<Mono>phone</Mono>, 'Phone'],
                        [<Mono>dateOfBirth</Mono>, 'Date of Birth (month / day / year selects)'],
                        [<Mono>gender</Mono>, 'Gender'],
                        [<Mono>heightCm, weightKg</Mono>, 'Height / Weight — only shown for patients aged 18 or under'],
                        [<Mono>address.*</Mono>, 'Shipping address (address lines, city, state, ZIP)'],
                      ]}
                    />
                  </div>
                </div>

                <div>
                  <SectionHeading>Normalization</SectionHeading>
                  <p className="text-secondary-600 dark:text-secondary-400 mt-2 mb-3">
                    Values are normalized before they are stored, so your formatting does not have to
                    match the checkout form.
                  </p>
                  <SimpleTable
                    headers={['Field', 'Accepted input', 'Stored as']}
                    rows={[
                      [<Mono>email</Mono>, 'Any casing, surrounding whitespace', <Mono>lower-cased, trimmed</Mono>],
                      [<Mono>phone</Mono>, <Mono>+1 (555) 123-4567 · 555.123.4567 · 15551234567</Mono>, <Mono>5551234567</Mono>],
                      [<Mono>dateOfBirth</Mono>, <Mono>1990-04-12 · 4/12/1990 · ISO datetime</Mono>, <Mono>1990-04-12</Mono>],
                      [<Mono>gender</Mono>, <Mono>Male · m · FEMALE · f</Mono>, <Mono>male / female</Mono>],
                      [<Mono>address.state</Mono>, <Mono>tx · TX</Mono>, <Mono>TX</Mono>],
                      [<Mono>address.zipCode</Mono>, <Mono>78701 · 78701-1234</Mono>, <Mono>78701</Mono>],
                      [<Mono>heightCm</Mono>, 'Centimeters', 'Converted to the feet / inches inputs'],
                      [<Mono>weightKg</Mono>, 'Kilograms', 'Converted to pounds'],
                    ]}
                  />
                </div>

                <Callout variant="warning" title="Malformed values fail the request">
                  <p>
                    Fields that are present must be valid. A bad value rejects the request with a{' '}
                    <Mono>400</Mono> rather than being silently dropped, so integration mistakes show
                    up when you create the session instead of at the customer's checkout. Every
                    problem found is reported in a single message.
                  </p>
                </Callout>

                <div>
                  <SectionHeading>Example</SectionHeading>
                  <div className="mt-3 space-y-3">
                    <CodeBlock
                      language="json"
                      title="A fully populated customer object"
                      code={`{
  "customer": {
    "firstName": "Jane",
    "lastName": "Doe",
    "email": "jane.doe@example.com",
    "phone": "+1 (555) 123-4567",
    "dateOfBirth": "1990-04-12",
    "gender": "female",
    "address": {
      "address1": "123 Main St",
      "address2": "Apt 4",
      "city": "Austin",
      "state": "TX",
      "zipCode": "78701"
    }
  }
}`}
                    />
                    <CodeBlock
                      language="bash"
                      title="Full request with customer pre-population"
                      code={`curl -X POST "https://api.capturehealth.io/partner/checkout" \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "frequency": 90,
    "items": [
      {
        "offeringId": "dosage-variety-uuid",
        "quantity": 6,
        "pricing": { "listPrice": 149.99, "finalPrice": 129.99 }
      }
    ],
    "customer": {
      "firstName": "Jane",
      "lastName": "Doe",
      "email": "jane.doe@example.com",
      "phone": "+1 (555) 123-4567",
      "dateOfBirth": "1990-04-12",
      "gender": "female",
      "address": {
        "address1": "123 Main St",
        "city": "Austin",
        "state": "TX",
        "zipCode": "78701"
      }
    },
    "shippingOptions": [
      {
        "carrier": "usps",
        "method": "usps_priority",
        "price": 9.99,
        "deliveryTimeframe": "2-3 business days"
      }
    ]
  }'`}
                    />
                  </div>
                </div>

                <Callout title="Send everything you have">
                  <p>
                    Every pre-filled field is one less thing the customer has to type, which measurably
                    lifts checkout completion. Partial data is fine — a name and an email alone still
                    help.
                  </p>
                </Callout>
              </div>
            </Card>
          )}

          {/* ------------------------------------------------------------- */}
          {/*  Response                                                     */}
          {/* ------------------------------------------------------------- */}
          {activeSection === 'response' && (
            <Card>
              <CardHeader title="Response" subtitle="What a successful checkout session returns" />

              <div className="space-y-6">
                <div>
                  <SectionHeading>Success — 200</SectionHeading>
                  <div className="mt-3">
                    <FieldTable
                      fields={[
                        { name: 'redirectUrl', type: 'string', required: true, description: 'Send the customer here. Carries the session token as ?sid= plus any UTM parameters from campaign.utmParameters.' },
                        { name: 'sessionId', type: 'string', required: true, description: 'The intake session ID, for your own records and support requests.' },
                      ]}
                    />
                  </div>
                  <div className="mt-3">
                    <CodeBlock
                      language="json"
                      title="Example response"
                      code={`{
  "redirectUrl": "https://intake.capturehealth.io?sid=YWJjZGVmZy0xMjM0...",
  "sessionId": "abcdefg-1234-5678-90ab-cdef12345678"
}`}
                    />
                  </div>
                </div>

                <div>
                  <SectionHeading>Redirecting the customer</SectionHeading>
                  <p className="text-secondary-600 dark:text-secondary-400 mt-2 mb-3">
                    Use the URL exactly as returned — the <Mono>sid</Mono> parameter is what loads the
                    cart, pricing, shipping options and any customer pre-fill values.
                  </p>
                  <CodeBlock
                    language="javascript"
                    title="Node.js"
                    code={`const response = await fetch('https://api.capturehealth.io/partner/checkout', {
  method: 'POST',
  headers: {
    'x-api-key': process.env.CAPTURE_HEALTH_API_KEY,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify(payload),
});

const result = await response.json();

if (!response.ok) {
  throw new Error(result.error);
}

// Hand the customer off to Capture Health
res.redirect(result.redirectUrl);`}
                  />
                </div>
              </div>
            </Card>
          )}

          {/* ------------------------------------------------------------- */}
          {/*  Errors                                                       */}
          {/* ------------------------------------------------------------- */}
          {activeSection === 'errors' && (
            <Card>
              <CardHeader title="Errors" subtitle="Validation rules and the messages they produce" />

              <div className="space-y-6">
                <div>
                  <SectionHeading>Error shape</SectionHeading>
                  <p className="text-secondary-600 dark:text-secondary-400 mt-2 mb-3">
                    Validation failures return <Mono>400</Mono> with a single human-readable message.
                    Nothing is created when a request fails.
                  </p>
                  <CodeBlock
                    language="json"
                    title="400 Bad Request"
                    code={`{
  "error": "Customer validation failed: email must be a valid email address"
}`}
                  />
                </div>

                <div>
                  <SectionHeading>Validation rules</SectionHeading>
                  <div className="mt-3 space-y-4">
                    <CollapsibleSection title="Cart & pricing" defaultOpen>
                      <ul className="list-disc list-inside space-y-1.5 text-sm text-secondary-600 dark:text-secondary-400">
                        <li>At least one <Mono>item</Mono> or <Mono>bundle</Mono> must be provided</li>
                        <li><Mono>frequency</Mono> must be a positive number</li>
                        <li><Mono>finalPrice</Mono> cannot exceed <Mono>listPrice</Mono></li>
                        <li><Mono>discountAmount</Mono> cannot be negative or exceed <Mono>listPrice</Mono></li>
                        <li>Every <Mono>offeringId</Mono> + <Mono>quantity</Mono> + month supply combination must match an active offering</li>
                      </ul>
                    </CollapsibleSection>

                    <CollapsibleSection title="Customer" badge="New">
                      <ul className="list-disc list-inside space-y-1.5 text-sm text-secondary-600 dark:text-secondary-400">
                        <li>Every field is optional, but any field that is present must be valid</li>
                        <li><Mono>email</Mono> must be a valid email address</li>
                        <li><Mono>phone</Mono> must contain 10 digits — a leading US country code is stripped</li>
                        <li><Mono>dateOfBirth</Mono> must be a real calendar date, in the past, within the last 120 years</li>
                        <li><Mono>gender</Mono> must resolve to <Mono>male</Mono> or <Mono>female</Mono></li>
                        <li><Mono>address</Mono>, when present, requires <Mono>address1</Mono>, <Mono>city</Mono>, <Mono>state</Mono> and <Mono>zipCode</Mono></li>
                        <li><Mono>address.state</Mono> must be a two-letter US state code; <Mono>address.country</Mono> must be US</li>
                        <li>All problems are reported together in one message</li>
                      </ul>
                    </CollapsibleSection>

                    <CollapsibleSection title="Shipping">
                      <ul className="list-disc list-inside space-y-1.5 text-sm text-secondary-600 dark:text-secondary-400">
                        <li>If provided, between 1 and 10 shipping options</li>
                        <li><Mono>carrier</Mono> must be <Mono>ups</Mono>, <Mono>usps</Mono> or <Mono>fedex</Mono>, with a matching method code</li>
                        <li><Mono>price</Mono> must be 0 or greater</li>
                        <li><Mono>discountAmount</Mono> must be between 0 and <Mono>price</Mono></li>
                      </ul>
                    </CollapsibleSection>
                  </div>
                </div>

                <div>
                  <SectionHeading>Common messages</SectionHeading>
                  <div className="mt-3">
                    <SimpleTable
                      headers={['Message', 'Cause']}
                      rows={[
                        [<Mono>At least one item or bundle must be provided</Mono>, 'Both items and bundles were empty'],
                        [<Mono>No active offering found for dosageVarietyId: X, quantity: Y, monthSupply: Z</Mono>, 'No offering matches that combination for your company'],
                        [<Mono>Final price cannot exceed list price for dosageVarietyId X</Mono>, 'finalPrice > listPrice'],
                        [<Mono>First charge amount cannot be negative for dosageVarietyId X</Mono>, 'discountAmount < 0'],
                        [<Mono>Shipping option at index N validation failed: …</Mono>, 'Invalid carrier, method or price'],
                        [<Mono>Customer validation failed: email must be a valid email address</Mono>, 'A malformed value in the customer object'],
                        [<Mono>Customer validation failed: address is missing required field(s): city, state</Mono>, 'A partial customer.address'],
                      ]}
                    />
                  </div>
                </div>

                <Callout variant="warning" title="Validate before you redirect">
                  <p>
                    Because a failed request creates no session, always check the response status
                    before redirecting. Surfacing the <Mono>error</Mono> message in your own logs makes
                    integration issues quick to diagnose.
                  </p>
                </Callout>
              </div>
            </Card>
          )}

          {/* ------------------------------------------------------------- */}
          {/*  Webhooks                                                     */}
          {/* ------------------------------------------------------------- */}
          {activeSection === 'wh-overview' && <WebhookOverview />}
          {activeSection === 'wh-configuration' && <WebhookConfiguration />}
          {activeSection === 'wh-security' && <WebhookSecurity />}
          {activeSection === 'wh-payload' && <WebhookPayload />}
          {activeSection === 'wh-events' && <WebhookEvents />}
          {activeSection === 'wh-attribution' && <WebhookAttribution />}
          {activeSection === 'wh-reliability' && <WebhookReliability />}
        </div>
      </div>
    </div>
  );
};

export default Documentation;
